import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import {
  FormField,
  IDropdownResponse,
  ILookupDropdownResponse,
} from 'src/app/shared/models/dynamic.model';
import { ValidatorService } from 'src/app/shared/services/form-control-validators.service';
import { businessInformationFormFields } from '../../accounts.config';
import { CommonService } from 'src/app/shared/services/common.service';
import {
  DataTypes,
  formFieldTypes,
  MasterLookup,
  ModeIcons,
  pageType,
  validationErrorMessages,
} from 'src/app/shared/utility/constants';
import { Store } from '@ngxs/store';
import { SaveBusinessDetails } from '../../store/accounts.action';
import { Observable, startWith, map } from 'rxjs';
import { IBusinessInfoDetailsData } from 'src/app/features/model/sm-accounts';
import { AccountsService } from 'src/app/features/service/accounts.service';

@Component({
  selector: 'app-business-information',
  templateUrl: './business-information.component.html',
  styleUrl: './business-information.component.scss',
})
export class BusinessInformationComponent implements OnInit {
  public accountBusinessInfoForm!: FormGroup;
  public formFields!: FormField[];
  private errorMessages: any;
  public setBusinessInfoWidth: any = {};
  public setBusinessInfoErrorTooltip: any = {};
  public setBusinessInfoTooltipEvent: any = {};
  public businessInfoTabData$!: Observable<IBusinessInfoDetailsData>;
  public businessInfoTabFormData: any;
  public pageMode!: string;

  // Payment Terms autocomplete properties
  public paymentTermsData!: IDropdownResponse[];
  public filterPaymentTermsOption!: Observable<IDropdownResponse[]>;
  public formFieldType = formFieldTypes;
  public isAutocompleteSelection = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly formUtilsService: ValidatorService,
    public readonly commonService: CommonService,
    private readonly fromUtilsService: ValidatorService,
    private readonly store: Store,
    private readonly accountsService: AccountsService
  ) {
    this.setReqInit();
    this.businessInfoTabData$ = this.store.select(
      state => state.accountsBusinessInfo
    );
  }

  ngOnInit() {
    this.loadLookUpData();
    this.dataFetch();
    this.checkMode();
    this.commonService.isUFOAccountRestrictedEdit
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyBusinessUfoFieldLocks());
    this.checkBusinessInfoValidData();
  }

  private createForm() {
    const { form, errorMessages } = this.formUtilsService.generateForm(
      businessInformationFormFields
    , this.destroyRef);
    this.accountBusinessInfoForm = form;
    this.errorMessages = errorMessages;
  }

  private setReqInit() {
    this.createForm();
    this.formFields = businessInformationFormFields.fields;
  }

  public checkControlForError(controlName: string) {
    return this.commonService.checkControlError(
      this.accountBusinessInfoForm,
      controlName
    );
  }

  public fetchWidth(event: MouseEvent, controlName: string) {
    this.commonService.setErrorTooltipData(
      event,
      controlName,
      this.setBusinessInfoWidth,
      this.setBusinessInfoTooltipEvent,
      this.setBusinessInfoErrorTooltip
    );
  }

  public fetchErrorMessages(controlName: string): string {
    if (
      this.accountBusinessInfoForm.get(controlName)?.errors?.[
        'autocompleteError'
      ]
    ) {
      return validationErrorMessages.autocompleteError;
    } else {
      return this.fromUtilsService.getErrorMessage(
        this.accountBusinessInfoForm,
        this.errorMessages,
        controlName
      );
    }
  }

  public dipatchBusinessFormChanges() {
    this.store.dispatch(
      new SaveBusinessDetails(this.accountBusinessInfoForm.value)
    );
    this.commonService.checkPristineAccordionData(false);
  }

  public onPaymentTermsAutocompleteSelection() {
    this.isAutocompleteSelection = true;
    this.dipatchBusinessFormChanges();
    // Reset the flag after a brief delay to allow for normal input validation
    setTimeout(() => {
      this.isAutocompleteSelection = false;
    }, 100);
  }

  public onPaymentTermsInput(event: any) {
    if (this.isAutocompleteSelection) {
      return;
    }

    const inputValue = event.target.value;
    const control = this.accountBusinessInfoForm.get('paymentTerms');

    if (!control || typeof inputValue !== 'string') {
      return;
    }

    const sanitizedValue = this.sanitizePaymentTermsValue(inputValue);
    this.updateControlValueIfChanged(control, inputValue, sanitizedValue);
    this.dipatchBusinessFormChanges();
  }

  private sanitizePaymentTermsValue(inputValue: string): string {
    let sanitizedValue = inputValue.replace(/\D/g, ''); // Remove all non-digits

    sanitizedValue = this.removeLeadingZeros(sanitizedValue);
    sanitizedValue = this.enforceMaxLimit(sanitizedValue);

    return sanitizedValue;
  }

  private removeLeadingZeros(value: string): string {
    if (value.length > 1 && value.startsWith('0')) {
      return value.replace(/^0+/, '');
    }
    return value;
  }

  private enforceMaxLimit(value: string): string {
    if (!value) {
      return value;
    }

    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 1000) {
      return '1000';
    }
    return value;
  }

  private updateControlValueIfChanged(
    control: any,
    originalValue: string,
    sanitizedValue: string
  ): void {
    if (originalValue !== sanitizedValue) {
      control.setValue(sanitizedValue, { emitEvent: false });
    }
  }

  public onPaymentTermsKeydown(event: KeyboardEvent) {
    if (!this.isAutocompleteSelection) {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down arrows
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'Home',
        'End',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ];

      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      if (
        event.ctrlKey &&
        ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())
      ) {
        return;
      }

      // Allow allowed keys
      if (allowedKeys.includes(event.key)) {
        return;
      }

      // Allow only numeric keys (0-9)
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    }
  }

  public onPaymentTermsPaste(event: ClipboardEvent) {
    if (!this.isAutocompleteSelection) {
      event.preventDefault();
      const pastedData = event.clipboardData?.getData('text') || '';
      const control = this.accountBusinessInfoForm.get('paymentTerms');

      if (control) {
        // Clean the pasted data - keep only numbers
        let sanitizedValue = pastedData.replace(/\D/g, '');

        // Prevent leading zeros except for "0" itself
        if (sanitizedValue.length > 1 && sanitizedValue.startsWith('0')) {
          sanitizedValue = sanitizedValue.replace(/^0+/, '');
        }

        // Check max limit
        if (sanitizedValue) {
          const numericValue = parseInt(sanitizedValue, 10);
          if (!isNaN(numericValue) && numericValue > 1000) {
            sanitizedValue = '1000';
          }
        }

        control.setValue(sanitizedValue);
        this.dipatchBusinessFormChanges();
      }
    }
  }

  private checkMode() {
    this.commonService.isCheckForMode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === ModeIcons.view) {
          this.pageMode = ModeIcons.view;
          this.accountBusinessInfoForm.disable();
        } else {
          this.pageMode = data != '' ? data : ModeIcons.add;
          this.accountBusinessInfoForm.enable();
        }
        this.applyBusinessUfoFieldLocks();
      });
  }

  private applyBusinessUfoFieldLocks(): void {
    if (this.pageMode === ModeIcons.view) {
      return;
    }
    const paymentTerms = this.accountBusinessInfoForm.get('paymentTerms');
    if (this.commonService.isUFOAccountRestrictedEdit.value) {
      paymentTerms?.disable({ emitEvent: false });
    } else {
      paymentTerms?.enable({ emitEvent: false });
    }
  }

  private dataFetch() {
    this.commonService.savedBagData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === pageType.dataBind) {
          this.fetchBusinessInfoTabData();
        }
      });
  }

  private getMasterLookups(requestType: string) {
    return new Promise((resolve, reject) => {
      try {
        this.commonService
          .getMasterLookups(requestType)
          .subscribe((response: ILookupDropdownResponse) => {
            if (response.data) {
              resolve(response.data);
            } else {
              resolve([]);
            }
          });
      } catch (error) {
        console.error('error in loading data', error);
      }
    });
  }

  private async loadLookUpData() {
    const [paymentTermsData]: any = await Promise.all([
      this.getMasterLookups(MasterLookup.paymentTerms),
    ]);
    this.paymentTermsData = paymentTermsData;
    this.initializePaymentTermsFilter();
  }

  private fetchBusinessInfoTabData() {
    this.businessInfoTabData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(businessInfoData => {
        this.businessInfoTabFormData = businessInfoData;
        this.accountBusinessInfoForm.patchValue(this.businessInfoTabFormData);
      });
  }

  private initializePaymentTermsFilter() {
    this.filterPaymentTermsOption = this.autocompleteValueChanges(
      'paymentTerms',
      this.paymentTermsData
    );
  }

  private autocompleteValueChanges(
    controlName: string,
    dropdownData: IDropdownResponse[]
  ): Observable<IDropdownResponse[]> {
    const formControl = this.accountBusinessInfoForm.get(
      controlName
    ) as FormControl;
    return formControl.valueChanges.pipe(
      startWith(''),
      map(val => {
        const filterValue =
          typeof val === DataTypes.String ? val.toLowerCase() : '';
        return dropdownData?.filter(
          item => item.name?.toLowerCase().includes(filterValue)
        );
      })
    );
  }

  private checkBusinessInfoValidData() {
    this.accountsService.isCheckForBusinessInfoData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data === true) {
          this.commonService.validateFormFields(this.accountBusinessInfoForm);
          // Set validation flag based on form validity
          this.accountsService.checkBusinessInfoDataValid(
            this.accountBusinessInfoForm.valid
          );
        } else {
          this.accountBusinessInfoForm.markAsPristine();
          this.accountsService.checkBusinessInfoDataValid(true); // Reset to valid when not checking
        }
      });
  }
}
